import { Suspense, lazy, useEffect, useRef, useState } from "react";
import type { z } from "zod";

import { ErrorApi, actualizarFlyer, rerenderizarFlyer } from "../api/client";
import type { Proyecto } from "../api/types";
import {
  FlyerDocumentSchema,
  getFlyerDimensions,
  type Anchor,
  type FlyerDocument,
  type GradientSpec,
  type GradientStop,
  type IconLayer,
  type Layer,
  type ShapeLayer,
  type TextBox,
  type TextLayer,
  type TextStyleName,
} from "../../../flyer-renderer/src/flyer/types";
import {
  estimateTextLayerMetrics,
  estimateTextWidthPx,
  textStyles,
} from "../../../flyer-renderer/src/flyer/theme";
import {
  familiaTieneItalica,
  resolveFontNames,
} from "../../../flyer-renderer/src/flyer/fontAllowlist";
import { CampoColorHex } from "./CampoColorHex";
import { ErrorBoundaryPreview } from "./ErrorBoundaryPreview";
import { PaletaColores } from "./PaletaColores";
import { PanelVersiones } from "./PanelVersiones";
import estilos from "./EditorVisualFlyer.module.css";

// Chunk async del preview vivo (WYSIWYG, restriccion vinculante 2): monta el
// componente `Flyer` REAL de flyer-renderer/src dentro de @remotion/player.
// Declarado a nivel de modulo para que el `import()` dinamico se dispare una
// unica vez, mismo patron que LivePreviewEscena en EditorVisualEscena.tsx
// del hermano "AI Video editor".
const LivePreview = lazy(() => import("./LivePreviewFlyer"));

interface Props {
  token: string;
  proyecto: Proyecto;
  onGuardado: (proyecto: Proyecto) => void;
  onCerrar: () => void;
}

const ETIQUETA_TIPO: Record<Layer["type"], string> = {
  text: "Texto",
  logo: "Logo",
  image: "Imagen",
  illustration: "Ilustración",
  icon: "Icono",
  chart_donut: "Gráfico",
  stars: "Estrellas",
  button: "Botón",
  shape: "Forma",
};

const ESTILOS_TEXTO_ETIQUETA: Record<TextStyleName, string> = {
  headline: "Titular",
  subtitle: "Subtítulo",
  body: "Cuerpo",
  price: "Precio",
  cta: "Llamado a la acción",
  caption: "Pie de foto",
};

const TEXT_STYLES: TextStyleName[] = [
  "headline",
  "subtitle",
  "body",
  "price",
  "cta",
  "caption",
];

// Mismo mapeo que ANCHOR_TRANSLATE/ANCHOR_ORIGIN de
// flyer-renderer/src/flyer/lib/positioning.ts (no exportados desde ahi): se
// duplica aca solo para que el overlay de seleccion se dibuje anclado igual
// que el render real. M2 (IM-5): grid de 9 anclajes -- Record<Anchor, ...>
// exige las 9 claves o tsc no compila.
const ANCHOR_TRANSLATE: Record<Anchor, string> = {
  top_left: "translate(0, 0)",
  top_center: "translate(-50%, 0)",
  top_right: "translate(-100%, 0)",
  center_left: "translate(0, -50%)",
  center: "translate(-50%, -50%)",
  center_right: "translate(-100%, -50%)",
  bottom_left: "translate(0, -100%)",
  bottom_center: "translate(-50%, -100%)",
  bottom_right: "translate(-100%, -100%)",
};
const ANCHOR_ORIGEN: Record<Anchor, string> = {
  top_left: "0 0",
  top_center: "50% 0",
  top_right: "100% 0",
  center_left: "0 50%",
  center: "50% 50%",
  center_right: "100% 50%",
  bottom_left: "0 100%",
  bottom_center: "50% 100%",
  bottom_right: "100% 100%",
};

// M3 (docs/plan-calidad-v3.md): las 9 opciones del grid 3x3 de anclaje del
// panel de posición, en el mismo orden fila-mayor que su CSS
// (grid-template-columns: repeat(3, ...)).
const ANCHOR_GRID: Array<{ valor: Anchor; etiqueta: string }> = [
  { valor: "top_left", etiqueta: "Arriba izquierda" },
  { valor: "top_center", etiqueta: "Arriba centro" },
  { valor: "top_right", etiqueta: "Arriba derecha" },
  { valor: "center_left", etiqueta: "Centro izquierda" },
  { valor: "center", etiqueta: "Centro" },
  { valor: "center_right", etiqueta: "Centro derecha" },
  { valor: "bottom_left", etiqueta: "Abajo izquierda" },
  { valor: "bottom_center", etiqueta: "Abajo centro" },
  { valor: "bottom_right", etiqueta: "Abajo derecha" },
];

// M3: 3 botones de alineación de texto (TextLayer.align).
const ALINEACIONES: Array<{
  valor: "left" | "center" | "right";
  etiqueta: string;
}> = [
  { valor: "left", etiqueta: "Izquierda" },
  { valor: "center", etiqueta: "Centro" },
  { valor: "right", etiqueta: "Derecha" },
];

// M3: pesos válidos de TextLayer.font_weight (múltiplo de 100 entre 300-900,
// espejo de TextLayerSchema.font_weight). "Automático" (sin selección) manda
// `undefined` -- hereda el peso fijo del text_style, comportamiento actual.
const PESOS_FUENTE = [300, 400, 500, 600, 700, 800, 900] as const;

const ESQUINAS: Array<{
  id: string;
  x: 0 | 1;
  y: 0 | 1;
  cursor: string;
  titulo: string;
}> = [
  {
    id: "no",
    x: 0,
    y: 0,
    cursor: "nwse-resize",
    titulo: "Redimensionar desde la esquina superior izquierda",
  },
  {
    id: "ne",
    x: 1,
    y: 0,
    cursor: "nesw-resize",
    titulo: "Redimensionar desde la esquina superior derecha",
  },
  {
    id: "so",
    x: 0,
    y: 1,
    cursor: "nesw-resize",
    titulo: "Redimensionar desde la esquina inferior izquierda",
  },
  {
    id: "se",
    x: 1,
    y: 1,
    cursor: "nwse-resize",
    titulo: "Redimensionar desde la esquina inferior derecha",
  },
];

// Presupuesto de tamaño del lienzo dentro del modal, en px CSS reales. Se usa
// una escala calculada en JS (no `aspect-ratio` CSS) porque el aspect del
// flyer varia por proyecto (1:1/4:5/9:16/16:9) y un <div> comun no deriva su
// ancho de forma confiable solo con max-height + aspect-ratio en todos los
// navegadores cuando ninguna dimension es explicita.
const ANCHO_MAX_LIENZO = 680;
const ALTO_MAX_LIENZO = 740;

interface TamanoCaja {
  ancho: number;
  alto: number;
}

const FACTOR_ANCHO_CAR = 0.54;

function clamp(valor: number, minimo: number, maximo: number): number {
  return Math.min(maximo, Math.max(minimo, valor));
}

function redondear(valor: number, decimales: number): number {
  const factor = 10 ** decimales;
  return Math.round(valor * factor) / factor;
}

function recortar(texto: string, maximo: number): string {
  return texto.length > maximo ? `${texto.slice(0, maximo - 1)}…` : texto;
}

function mensajeDeError(err: unknown, generico: string): string {
  if (err instanceof ErrorApi) return err.message;
  if (err instanceof Error) return err.message;
  return generico;
}

function formatearErroresZod(error: z.ZodError): string {
  const primero = error.issues[0];
  if (!primero) return "El documento del flyer no es válido.";
  const ruta = primero.path.join(".") || "documento";
  const resto = error.issues.length - 1;
  const base = `${ruta}: ${primero.message}`;
  return resto > 0
    ? `${base} (+${resto} error${resto === 1 ? "" : "es"} más)`
    : base;
}

// Tamaños de caja de seleccion en px REALES del canvas (sin reescalar por
// formato): mismos numeros que dibujan flyer-renderer/src/flyer/components/*
// (LogoLayer 300-560px, IconLayer 200px, etc.) salvo texto, que reutiliza
// `estimateTextLayerMetrics`/`estimateTextWidthPx` de theme.ts (M3,
// docs/plan-calidad-v3.md) -- las MISMAS formulas que decide `fit_font_size`
// para el render real, en vez de duplicar esa logica en el editor.
function medirCajaTexto(layer: TextLayer, canvasWidth: number): TamanoCaja {
  const uppercase = layer.text_transform === "uppercase";
  const metrics = estimateTextLayerMetrics({
    content: layer.content,
    style: layer.text_style,
    maxWidth: layer.max_width,
    canvasWidth,
    sizeFactor: layer.size_factor,
    letterSpacingEm: layer.letter_spacing_em,
    uppercase,
    lineHeight: layer.line_height,
    box: layer.box ? { paddingYEm: layer.box.padding_y_em } : null,
  });
  const maxWidthPx = layer.max_width * canvasWidth;
  if (!layer.box) {
    // Sin caja: TextLayer.tsx fija `width: maxWidthPx` siempre (AV-2), sin
    // importar cuantas lineas ocupe el texto real.
    return { ancho: maxWidthPx, alto: metrics.height };
  }
  // Con caja: el wrapper es `width: fit-content` + `maxWidth: maxWidthPx`
  // (TextLayer.tsx) -- abraza el texto real hasta ese tope, mas el padding
  // horizontal en `em` del propio wrapper (que a su vez tambien es fit-content).
  const anchoTexto = Math.min(
    maxWidthPx,
    estimateTextWidthPx(
      layer.content,
      metrics.fontSize,
      layer.letter_spacing_em,
      uppercase,
    ),
  );
  const ancho = anchoTexto + 2 * layer.box.padding_x_em * metrics.fontSize;
  return { ancho, alto: metrics.height };
}

const TAMANOS_FIJOS: Partial<Record<Layer["type"], TamanoCaja>> = {
  illustration: { ancho: 420, alto: 420 },
  chart_donut: { ancho: 560, alto: 600 },
  stars: { ancho: 638, alto: 110 },
};

function medirCaja(
  layer: Layer,
  canvasWidth: number,
  canvasHeight: number,
): TamanoCaja {
  switch (layer.type) {
    case "text":
      return medirCajaTexto(layer, canvasWidth);
    case "logo":
      return layer.asset_src
        ? { ancho: 560, alto: 460 }
        : { ancho: 340, alto: 420 };
    case "image": {
      const ancho = layer.width * canvasWidth;
      return { ancho, alto: ancho * 0.66 + (layer.caption ? 70 : 0) };
    }
    case "button":
      return {
        ancho: layer.content.length * 46 * FACTOR_ANCHO_CAR + 128,
        alto: 46 + 68,
      };
    case "shape":
      return {
        ancho: layer.width * canvasWidth,
        alto: layer.height * canvasHeight,
      };
    case "icon": {
      // M2 (IM-5): respeta `size` (fraccion del ANCHO del lienzo) si esta
      // presente -- mismo calculo que IconLayer.tsx. Ausente => 200px fijos.
      const lado = layer.size ? layer.size * canvasWidth : 200;
      return { ancho: lado, alto: lado };
    }
    default:
      return TAMANOS_FIJOS[layer.type] ?? { ancho: 300, alto: 300 };
  }
}

type EstadoArrastre =
  | {
      modo: "mover";
      id: string;
      xInicial: number;
      yInicial: number;
      punteroX: number;
      punteroY: number;
    }
  | {
      modo: "escalar";
      id: string;
      escalaInicial: number;
      centroXpx: number;
      centroYpx: number;
      distanciaInicial: number;
    };

// Editor visual del flyer (M4): lienzo con preview vivo (@remotion/player
// montando el componente `Flyer` real => WYSIWYG con el render final),
// arrastrar/redimensionar capas en coordenadas normalizadas 0-1, panel de
// propiedades de la capa activa, paleta de marca y panel de versiones. Un
// unico documento en memoria (copia validada al abrir); "Guardar" es el
// unico PATCH.
export function EditorVisualFlyer({
  token,
  proyecto,
  onGuardado,
  onCerrar,
}: Props): JSX.Element {
  const analisisInicial = FlyerDocumentSchema.safeParse(
    proyecto.flyer_document,
  );
  const [documento, setDocumento] = useState<FlyerDocument | null>(() =>
    analisisInicial.success ? analisisInicial.data : null,
  );
  const [errorInicial] = useState<string | null>(() =>
    analisisInicial.success ? null : formatearErroresZod(analisisInicial.error),
  );
  const ultimoGuardadoRef = useRef<FlyerDocument | null>(documento);

  const [proyectoLocal, setProyectoLocal] = useState<Proyecto>(proyecto);
  const [seleccionId, setSeleccionId] = useState<string | null>(null);
  const [arrastre, setArrastre] = useState<EstadoArrastre | null>(null);
  const [guardando, setGuardando] = useState(false);
  const [errorGuardado, setErrorGuardado] = useState<string | null>(null);
  const [rerenderizando, setRerenderizando] = useState(false);
  const [errorRender, setErrorRender] = useState<string | null>(null);
  const [previewRoto, setPreviewRoto] = useState(false);
  const [disparadorVersiones, setDisparadorVersiones] = useState(0);

  const canvasRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const botonCerrarRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    botonCerrarRef.current?.focus();
  }, []);

  const hayCambios =
    documento !== null &&
    JSON.stringify(documento) !== JSON.stringify(ultimoGuardadoRef.current);

  const aplicarProyectoActualizado = (p: Proyecto): void => {
    const parseado = FlyerDocumentSchema.safeParse(p.flyer_document);
    if (parseado.success) {
      setDocumento(parseado.data);
      ultimoGuardadoRef.current = parseado.data;
    }
    setProyectoLocal(p);
    onGuardado(p);
  };

  const actualizarCapa = (id: string, siguiente: Layer): void => {
    setDocumento((prev) =>
      prev
        ? {
            ...prev,
            layers: prev.layers.map((l) => (l.id === id ? siguiente : l)),
          }
        : prev,
    );
  };

  const guardar = async (): Promise<void> => {
    if (!documento || guardando) return;
    const parseado = FlyerDocumentSchema.safeParse(documento);
    if (!parseado.success) {
      setErrorGuardado(formatearErroresZod(parseado.error));
      return;
    }
    setGuardando(true);
    setErrorGuardado(null);
    try {
      const resp = await actualizarFlyer(token, proyecto.id, parseado.data);
      aplicarProyectoActualizado(resp.project);
      setDisparadorVersiones((n) => n + 1);
    } catch (err) {
      setErrorGuardado(mensajeDeError(err, "No se pudo guardar el flyer"));
    } finally {
      setGuardando(false);
    }
  };

  const rerenderizar = async (): Promise<void> => {
    if (rerenderizando || guardando || hayCambios) return;
    setRerenderizando(true);
    setErrorRender(null);
    try {
      const resp = await rerenderizarFlyer(token, proyecto.id);
      const actualizado: Proyecto = {
        ...proyectoLocal,
        png_url: resp.png_url,
      };
      setProyectoLocal(actualizado);
      onGuardado(actualizado);
    } catch (err) {
      setErrorRender(mensajeDeError(err, "No se pudo re-renderizar el flyer"));
    } finally {
      setRerenderizando(false);
    }
  };

  const alRestaurar = (p: Proyecto): void => {
    setSeleccionId(null);
    setErrorGuardado(null);
    aplicarProyectoActualizado(p);
    setDisparadorVersiones((n) => n + 1);
  };

  const iniciarMover = (
    id: string,
    e: React.PointerEvent<HTMLDivElement>,
  ): void => {
    if (guardando || !documento) return;
    e.stopPropagation();
    const capa = documento.layers.find((l) => l.id === id);
    if (!capa) return;
    setSeleccionId(id);
    e.currentTarget.setPointerCapture(e.pointerId);
    setArrastre({
      modo: "mover",
      id,
      xInicial: capa.position.x,
      yInicial: capa.position.y,
      punteroX: e.clientX,
      punteroY: e.clientY,
    });
  };

  const iniciarEscalar = (
    id: string,
    e: React.PointerEvent<HTMLDivElement>,
  ): void => {
    if (guardando || !documento) return;
    e.stopPropagation();
    const capa = documento.layers.find((l) => l.id === id);
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!capa || !rect) return;
    setSeleccionId(id);
    e.currentTarget.setPointerCapture(e.pointerId);
    const centroXpx = rect.left + capa.position.x * rect.width;
    const centroYpx = rect.top + capa.position.y * rect.height;
    // "|| 1": si el puntero cae exacto en el centro (distancia 0), el
    // cociente de escala dividiria por cero; 1px como base inocua.
    const distanciaInicial =
      Math.hypot(e.clientX - centroXpx, e.clientY - centroYpx) || 1;
    setArrastre({
      modo: "escalar",
      id,
      escalaInicial: capa.scale,
      centroXpx,
      centroYpx,
      distanciaInicial,
    });
  };

  const alMoverPuntero = (e: React.PointerEvent<HTMLDivElement>): void => {
    if (!arrastre || !documento) return;
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;
    const capa = documento.layers.find((l) => l.id === arrastre.id);
    if (!capa) return;
    if (arrastre.modo === "mover") {
      const deltaX = (e.clientX - arrastre.punteroX) / rect.width;
      const deltaY = (e.clientY - arrastre.punteroY) / rect.height;
      const x = redondear(clamp(arrastre.xInicial + deltaX, 0, 1), 4);
      const y = redondear(clamp(arrastre.yInicial + deltaY, 0, 1), 4);
      actualizarCapa(capa.id, {
        ...capa,
        position: { ...capa.position, x, y },
      });
    } else {
      const distanciaActual = Math.hypot(
        e.clientX - arrastre.centroXpx,
        e.clientY - arrastre.centroYpx,
      );
      const factor = distanciaActual / arrastre.distanciaInicial;
      const nuevaEscala = clamp(
        redondear(arrastre.escalaInicial * factor, 2),
        0.3,
        1.6,
      );
      actualizarCapa(capa.id, { ...capa, scale: nuevaEscala });
    }
  };

  const alSoltarPuntero = (): void => setArrastre(null);

  const alTeclaEnCaja = (
    capa: Layer,
    e: React.KeyboardEvent<HTMLDivElement>,
  ): void => {
    if (guardando) return;
    const paso = e.shiftKey ? 0.05 : 0.01;
    switch (e.key) {
      case "ArrowUp":
        e.preventDefault();
        actualizarCapa(capa.id, {
          ...capa,
          position: {
            ...capa.position,
            y: redondear(clamp(capa.position.y - paso, 0, 1), 4),
          },
        });
        break;
      case "ArrowDown":
        e.preventDefault();
        actualizarCapa(capa.id, {
          ...capa,
          position: {
            ...capa.position,
            y: redondear(clamp(capa.position.y + paso, 0, 1), 4),
          },
        });
        break;
      case "ArrowLeft":
        e.preventDefault();
        actualizarCapa(capa.id, {
          ...capa,
          position: {
            ...capa.position,
            x: redondear(clamp(capa.position.x - paso, 0, 1), 4),
          },
        });
        break;
      case "ArrowRight":
        e.preventDefault();
        actualizarCapa(capa.id, {
          ...capa,
          position: {
            ...capa.position,
            x: redondear(clamp(capa.position.x + paso, 0, 1), 4),
          },
        });
        break;
      case "+":
      case "=":
        e.preventDefault();
        actualizarCapa(capa.id, {
          ...capa,
          scale: clamp(redondear(capa.scale + 0.05, 2), 0.3, 1.6),
        });
        break;
      case "-":
      case "_":
        e.preventDefault();
        actualizarCapa(capa.id, {
          ...capa,
          scale: clamp(redondear(capa.scale - 0.05, 2), 0.3, 1.6),
        });
        break;
      case "Enter":
      case " ":
        e.preventDefault();
        setSeleccionId(capa.id);
        break;
      default:
        break;
    }
  };

  const intentarCerrar = (): void => {
    if (guardando) return;
    if (hayCambios) {
      const ok = window.confirm("Hay cambios sin guardar. ¿Descartarlos?");
      if (!ok) return;
    }
    onCerrar();
  };

  const atraparFoco = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    const panel = panelRef.current;
    if (!panel) return;
    const focosables = panel.querySelectorAll<HTMLElement>(
      'button:not(:disabled), [href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])',
    );
    if (focosables.length === 0) return;
    const primero = focosables[0];
    const ultimo = focosables[focosables.length - 1];
    if (e.shiftKey && document.activeElement === primero) {
      e.preventDefault();
      ultimo.focus();
    } else if (!e.shiftKey && document.activeElement === ultimo) {
      e.preventDefault();
      primero.focus();
    }
  };

  const alTeclaPanel = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    if (e.key === "Escape") {
      e.stopPropagation();
      intentarCerrar();
    } else if (e.key === "Tab") {
      atraparFoco(e);
    }
  };

  const idTitulo = `editor-flyer-titulo-${proyecto.id}`;
  const capaSeleccionada =
    documento?.layers.find((l) => l.id === seleccionId) ?? null;
  // M3: familia EFECTIVA (heading/body segun el rol del text_style) de la
  // capa de texto seleccionada -- misma resolucion que TextLayer.tsx, para
  // deshabilitar el toggle de cursiva cuando esa familia no tiene italica
  // real (AV-6).
  const familiaActivaTexto =
    capaSeleccionada?.type === "text"
      ? resolveFontNames(documento?.brand)[
          textStyles[capaSeleccionada.text_style].role
        ]
      : null;
  const permiteItalicaCapa = familiaActivaTexto
    ? familiaTieneItalica(familiaActivaTexto)
    : false;
  const { width: canvasWidth, height: canvasHeight } = documento
    ? getFlyerDimensions(documento.format.aspect)
    : { width: 1080, height: 1080 };
  const escalaLienzo = Math.min(
    ANCHO_MAX_LIENZO / canvasWidth,
    ALTO_MAX_LIENZO / canvasHeight,
    1,
  );
  const anchoMostrado = canvasWidth * escalaLienzo;
  const altoMostrado = canvasHeight * escalaLienzo;

  return (
    <div
      className={estilos.overlay}
      onPointerDown={(e) => {
        if (e.target === e.currentTarget) intentarCerrar();
      }}
    >
      <div
        ref={panelRef}
        className={estilos.panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby={idTitulo}
        tabIndex={-1}
        onKeyDown={alTeclaPanel}
      >
        <div className={estilos.encabezado}>
          <div>
            <h2 id={idTitulo}>Editar flyer</h2>
            <p className={estilos.subtitulo}>
              Arrastra un elemento para moverlo o usa sus tiradores para
              escalarlo. Los cambios se ven en vivo, igual que el render final.
            </p>
          </div>
          <button
            ref={botonCerrarRef}
            type="button"
            className={estilos.botonCerrar}
            onClick={intentarCerrar}
            disabled={guardando}
            aria-label="Cerrar editor de flyer"
          >
            ×
          </button>
        </div>

        {!documento ? (
          <div className={estilos.vacio} role="alert">
            {errorInicial ??
              "Este proyecto todavía no tiene un flyer generado para editar."}
          </div>
        ) : (
          <div
            className={estilos.cuerpo}
            style={
              guardando ? { opacity: 0.6, pointerEvents: "none" } : undefined
            }
          >
            <div className={estilos.lienzoColumna}>
              <div
                className={estilos.lienzoContenedor}
                ref={canvasRef}
                style={{ width: anchoMostrado, height: altoMostrado }}
              >
                {proyectoLocal.png_url && (
                  <img
                    src={proyectoLocal.png_url}
                    alt=""
                    aria-hidden="true"
                    className={estilos.lienzoFondo}
                  />
                )}
                <div className={estilos.lienzoPreviewVivo} aria-hidden="true">
                  <ErrorBoundaryPreview
                    onError={() => setPreviewRoto(true)}
                    fallback={null}
                  >
                    <Suspense fallback={null}>
                      {!previewRoto && <LivePreview flyer={documento} />}
                    </Suspense>
                  </ErrorBoundaryPreview>
                </div>
                <div
                  className={estilos.overlayCapas}
                  onPointerDown={() => setSeleccionId(null)}
                  onPointerMove={alMoverPuntero}
                  onPointerUp={alSoltarPuntero}
                  onPointerCancel={alSoltarPuntero}
                >
                  {documento.layers.map((capa) => {
                    const tam = medirCaja(capa, canvasWidth, canvasHeight);
                    const seleccionada = capa.id === seleccionId;
                    const anchor = capa.position.anchor;
                    const etiqueta = ETIQUETA_TIPO[capa.type];
                    const previsualizacion =
                      "content" in capa ? recortar(capa.content, 30) : null;
                    const estiloCaja: React.CSSProperties = {
                      left: `${capa.position.x * 100}%`,
                      top: `${capa.position.y * 100}%`,
                      width: `${(tam.ancho / canvasWidth) * 100}%`,
                      height: `${(tam.alto / canvasHeight) * 100}%`,
                      // rotate() al final para que la caja de selección rote
                      // igual que la preview viva (mismo orden que
                      // flyer-renderer/src/flyer/lib/positioning.ts).
                      transform: `${ANCHOR_TRANSLATE[anchor]} scale(${capa.scale}) rotate(${capa.rotation_deg ?? 0}deg)`,
                      transformOrigin: ANCHOR_ORIGEN[anchor],
                      zIndex: seleccionada ? 999 : Math.max(1, capa.z + 200),
                    };
                    return (
                      <div
                        key={capa.id}
                        className={`${estilos.caja} ${
                          seleccionada ? estilos.cajaSeleccionada : ""
                        }`}
                        style={estiloCaja}
                        role="button"
                        tabIndex={0}
                        aria-pressed={seleccionada}
                        aria-label={
                          previsualizacion
                            ? `${etiqueta}: ${previsualizacion}`
                            : etiqueta
                        }
                        onPointerDown={(e) => iniciarMover(capa.id, e)}
                        onKeyDown={(e) => alTeclaEnCaja(capa, e)}
                      >
                        <span className={estilos.etiquetaCapa}>{etiqueta}</span>
                        {seleccionada &&
                          ESQUINAS.map((esquina) => (
                            <div
                              key={esquina.id}
                              className={estilos.manija}
                              style={{
                                left: `${esquina.x * 100}%`,
                                top: `${esquina.y * 100}%`,
                                transform: `translate(-50%, -50%) scale(${
                                  1 / capa.scale
                                })`,
                                cursor: esquina.cursor,
                              }}
                              title={esquina.titulo}
                              onPointerDown={(e) => iniciarEscalar(capa.id, e)}
                            />
                          ))}
                      </div>
                    );
                  })}
                </div>
              </div>

              {proyectoLocal.png_url && (
                <a
                  className="boton boton-secundario"
                  href={proyectoLocal.png_url}
                  target="_blank"
                  rel="noreferrer"
                >
                  Abrir PNG actual
                </a>
              )}
            </div>

            <div className={estilos.panelDerecho}>
              <section className={estilos.seccion}>
                <h3>Capas ({documento.layers.length})</h3>
                <ul className={estilos.listaCapas} aria-label="Capas del flyer">
                  {documento.layers.map((capa, i) => {
                    const seleccionada = capa.id === seleccionId;
                    const previsualizacion =
                      "content" in capa ? recortar(capa.content, 40) : null;
                    return (
                      <li key={capa.id}>
                        <button
                          type="button"
                          className={`${estilos.filaCapa} ${
                            seleccionada ? estilos.filaCapaActiva : ""
                          }`}
                          aria-pressed={seleccionada}
                          onClick={() => setSeleccionId(capa.id)}
                        >
                          <span className={estilos.filaCapaIndice}>
                            {i + 1}
                          </span>
                          <span className={estilos.filaCapaTipo}>
                            {ETIQUETA_TIPO[capa.type]}
                          </span>
                          {previsualizacion && (
                            <span className={estilos.filaCapaContenido}>
                              "{previsualizacion}"
                            </span>
                          )}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </section>

              <section
                className={estilos.seccion}
                aria-labelledby="titulo-propiedades"
              >
                <h3 id="titulo-propiedades">Propiedades</h3>
                {!capaSeleccionada ? (
                  <p className={estilos.sinSeleccion}>
                    Selecciona un elemento del lienzo o de la lista de capas
                    para editarlo.
                  </p>
                ) : (
                  <div className={estilos.propiedades}>
                    <p className={estilos.tipoCapa}>
                      {ETIQUETA_TIPO[capaSeleccionada.type]}
                    </p>

                    {(capaSeleccionada.type === "text" ||
                      capaSeleccionada.type === "button") && (
                      <div className={estilos.campoPropiedad}>
                        <label htmlFor="prop-contenido">Texto</label>
                        <textarea
                          id="prop-contenido"
                          value={capaSeleccionada.content}
                          maxLength={
                            capaSeleccionada.type === "text" ? 500 : 40
                          }
                          disabled={guardando}
                          onChange={(e) =>
                            actualizarCapa(capaSeleccionada.id, {
                              ...capaSeleccionada,
                              content: e.target.value,
                            })
                          }
                        />
                      </div>
                    )}

                    {capaSeleccionada.type === "text" && (
                      <div className={estilos.campoPropiedad}>
                        <label htmlFor="prop-estilo">Estilo de texto</label>
                        <select
                          id="prop-estilo"
                          value={capaSeleccionada.text_style}
                          disabled={guardando}
                          onChange={(e) =>
                            actualizarCapa(capaSeleccionada.id, {
                              ...capaSeleccionada,
                              text_style: e.target.value as TextStyleName,
                            })
                          }
                        >
                          {TEXT_STYLES.map((s) => (
                            <option key={s} value={s}>
                              {ESTILOS_TEXTO_ETIQUETA[s]}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    {capaSeleccionada.type === "text" && (
                      <div className={estilos.campoPropiedad}>
                        <CampoColorHex
                          id="prop-color"
                          etiqueta="Color del texto"
                          valor={
                            capaSeleccionada.color ??
                            documento.brand?.colors.text ??
                            "#000000"
                          }
                          onChange={(valor) =>
                            actualizarCapa(capaSeleccionada.id, {
                              ...capaSeleccionada,
                              color: valor,
                            })
                          }
                          deshabilitado={guardando}
                        />
                        {capaSeleccionada.color && (
                          <button
                            type="button"
                            className="boton boton-secundario"
                            onClick={() =>
                              actualizarCapa(capaSeleccionada.id, {
                                ...capaSeleccionada,
                                color: undefined,
                              })
                            }
                            disabled={guardando}
                          >
                            Usar color de marca
                          </button>
                        )}
                      </div>
                    )}

                    {capaSeleccionada.type === "text" && (
                      <>
                        <div className={estilos.campoPropiedad}>
                          <span id="titulo-align">Alineación del texto</span>
                          <div
                            className={estilos.segmentado}
                            role="group"
                            aria-labelledby="titulo-align"
                          >
                            {ALINEACIONES.map((op) => (
                              <button
                                key={op.valor}
                                type="button"
                                className={`${estilos.opcionSegmento} ${
                                  capaSeleccionada.align === op.valor
                                    ? estilos.opcionSegmentoActiva
                                    : ""
                                }`}
                                aria-pressed={
                                  capaSeleccionada.align === op.valor
                                }
                                disabled={guardando}
                                onClick={() =>
                                  actualizarCapa(capaSeleccionada.id, {
                                    ...capaSeleccionada,
                                    align: op.valor,
                                  })
                                }
                              >
                                {op.etiqueta}
                              </button>
                            ))}
                          </div>
                        </div>

                        <div className={estilos.campoPropiedad}>
                          <span id="titulo-cursiva">Estilo de letra</span>
                          <button
                            type="button"
                            className={`${estilos.botonToggle} ${
                              capaSeleccionada.italic
                                ? estilos.botonToggleActivo
                                : ""
                            }`}
                            aria-pressed={capaSeleccionada.italic}
                            aria-labelledby="titulo-cursiva"
                            disabled={guardando || !permiteItalicaCapa}
                            title={
                              permiteItalicaCapa
                                ? undefined
                                : `${familiaActivaTexto ?? "Esta familia"} no tiene una cursiva real disponible`
                            }
                            onClick={() =>
                              actualizarCapa(capaSeleccionada.id, {
                                ...capaSeleccionada,
                                italic: !capaSeleccionada.italic,
                              })
                            }
                          >
                            Cursiva
                          </button>
                          {!permiteItalicaCapa && (
                            <p className={estilos.notaControl}>
                              {familiaActivaTexto} no tiene una itálica real;
                              este control queda deshabilitado.
                            </p>
                          )}
                        </div>

                        <div className={estilos.campoPropiedad}>
                          <span id="titulo-mayus">Transformación</span>
                          <button
                            type="button"
                            className={`${estilos.botonToggle} ${
                              capaSeleccionada.text_transform === "uppercase"
                                ? estilos.botonToggleActivo
                                : ""
                            }`}
                            aria-pressed={
                              capaSeleccionada.text_transform === "uppercase"
                            }
                            aria-labelledby="titulo-mayus"
                            disabled={guardando}
                            onClick={() =>
                              actualizarCapa(capaSeleccionada.id, {
                                ...capaSeleccionada,
                                text_transform:
                                  capaSeleccionada.text_transform ===
                                  "uppercase"
                                    ? "none"
                                    : "uppercase",
                              })
                            }
                          >
                            MAYÚS
                          </button>
                        </div>

                        <div className={estilos.campoPropiedad}>
                          <label htmlFor="prop-peso">Peso de la fuente</label>
                          <select
                            id="prop-peso"
                            value={String(
                              capaSeleccionada.font_weight ?? "auto",
                            )}
                            disabled={guardando}
                            onChange={(e) =>
                              actualizarCapa(capaSeleccionada.id, {
                                ...capaSeleccionada,
                                font_weight:
                                  e.target.value === "auto"
                                    ? undefined
                                    : Number(e.target.value),
                              })
                            }
                          >
                            <option value="auto">
                              Automático (
                              {
                                textStyles[capaSeleccionada.text_style]
                                  .fontWeight
                              }
                              )
                            </option>
                            {PESOS_FUENTE.map((peso) => (
                              <option key={peso} value={peso}>
                                {peso}
                              </option>
                            ))}
                          </select>
                        </div>

                        <CampoSlider
                          id="prop-tracking"
                          etiqueta={`Espaciado entre letras (${capaSeleccionada.letter_spacing_em.toFixed(2)}em)`}
                          min={0}
                          max={0.5}
                          step={0.01}
                          valor={capaSeleccionada.letter_spacing_em}
                          deshabilitado={guardando}
                          onChange={(valor) =>
                            actualizarCapa(capaSeleccionada.id, {
                              ...capaSeleccionada,
                              letter_spacing_em: valor,
                            })
                          }
                        />

                        <div className={estilos.campoPropiedad}>
                          <div className={estilos.filaEtiquetaToggle}>
                            <label htmlFor="prop-line-height">
                              Interlineado
                              {capaSeleccionada.line_height == null
                                ? ` (automático ≈ ${textStyles[
                                    capaSeleccionada.text_style
                                  ].lineHeight.toFixed(2)})`
                                : ` (${capaSeleccionada.line_height.toFixed(2)})`}
                            </label>
                            <button
                              type="button"
                              className={`${estilos.botonToggle} ${
                                capaSeleccionada.line_height == null
                                  ? estilos.botonToggleActivo
                                  : ""
                              }`}
                              aria-pressed={
                                capaSeleccionada.line_height == null
                              }
                              disabled={guardando}
                              onClick={() =>
                                actualizarCapa(capaSeleccionada.id, {
                                  ...capaSeleccionada,
                                  line_height:
                                    capaSeleccionada.line_height == null
                                      ? textStyles[capaSeleccionada.text_style]
                                          .lineHeight
                                      : undefined,
                                })
                              }
                            >
                              Auto
                            </button>
                          </div>
                          <input
                            id="prop-line-height"
                            type="range"
                            min={0.8}
                            max={2.0}
                            step={0.02}
                            value={
                              capaSeleccionada.line_height ??
                              textStyles[capaSeleccionada.text_style].lineHeight
                            }
                            disabled={
                              guardando || capaSeleccionada.line_height == null
                            }
                            onChange={(e) =>
                              actualizarCapa(capaSeleccionada.id, {
                                ...capaSeleccionada,
                                line_height: Number(e.target.value),
                              })
                            }
                          />
                        </div>

                        <CampoSlider
                          id="prop-size-factor"
                          etiqueta={`Tamaño de fuente (${capaSeleccionada.size_factor.toFixed(2)}×)`}
                          min={0.6}
                          max={2.0}
                          step={0.05}
                          valor={capaSeleccionada.size_factor}
                          deshabilitado={guardando}
                          onChange={(valor) =>
                            actualizarCapa(capaSeleccionada.id, {
                              ...capaSeleccionada,
                              size_factor: valor,
                            })
                          }
                        />

                        <div className={estilos.campoPropiedad}>
                          <label htmlFor="prop-sombra">Sombra de texto</label>
                          <select
                            id="prop-sombra"
                            value={capaSeleccionada.text_shadow}
                            disabled={guardando}
                            onChange={(e) =>
                              actualizarCapa(capaSeleccionada.id, {
                                ...capaSeleccionada,
                                text_shadow: e.target
                                  .value as TextLayer["text_shadow"],
                              })
                            }
                          >
                            <option value="none">Sin sombra</option>
                            <option value="soft">Suave</option>
                            <option value="strong">Fuerte</option>
                          </select>
                        </div>

                        <details className={estilos.grupoColapsable}>
                          <summary className={estilos.grupoColapsableResumen}>
                            Caja / píldora
                            {capaSeleccionada.box ? "" : " (sin usar)"}
                          </summary>
                          <div className={estilos.grupoColapsableCuerpo}>
                            <ControlesCajaTexto
                              capa={capaSeleccionada}
                              deshabilitado={guardando}
                              onCambiar={(box) =>
                                actualizarCapa(capaSeleccionada.id, {
                                  ...capaSeleccionada,
                                  box,
                                })
                              }
                            />
                          </div>
                        </details>
                      </>
                    )}

                    {capaSeleccionada.type === "shape" && (
                      <div className={estilos.campoPropiedad}>
                        <CampoColorHex
                          id="prop-color"
                          etiqueta="Color de la forma"
                          valor={capaSeleccionada.color}
                          onChange={(valor) =>
                            actualizarCapa(capaSeleccionada.id, {
                              ...capaSeleccionada,
                              color: valor,
                            })
                          }
                          deshabilitado={guardando}
                        />
                      </div>
                    )}

                    {capaSeleccionada.type === "shape" && (
                      <PropiedadesForma
                        capa={capaSeleccionada}
                        deshabilitado={guardando}
                        onCambiar={(siguiente) =>
                          actualizarCapa(capaSeleccionada.id, siguiente)
                        }
                      />
                    )}

                    {capaSeleccionada.type === "icon" && (
                      <ControlesTamanoIcono
                        capa={capaSeleccionada}
                        canvasWidth={canvasWidth}
                        deshabilitado={guardando}
                        onCambiar={(siguiente) =>
                          actualizarCapa(capaSeleccionada.id, siguiente)
                        }
                      />
                    )}

                    <div className={estilos.campoPropiedad}>
                      <label htmlFor="prop-escala">
                        Tamaño (escala: {capaSeleccionada.scale.toFixed(2)}×)
                      </label>
                      <input
                        id="prop-escala"
                        type="range"
                        min={0.3}
                        max={1.6}
                        step={0.05}
                        value={capaSeleccionada.scale}
                        disabled={guardando}
                        onChange={(e) =>
                          actualizarCapa(capaSeleccionada.id, {
                            ...capaSeleccionada,
                            scale: Number(e.target.value),
                          })
                        }
                      />
                    </div>

                    <div className={estilos.campoPropiedad}>
                      <span id="titulo-anchor">Punto de anclaje</span>
                      <div
                        className={estilos.grillaAnclaje}
                        role="group"
                        aria-labelledby="titulo-anchor"
                      >
                        {ANCHOR_GRID.map((opcion) => (
                          <button
                            key={opcion.valor}
                            type="button"
                            className={`${estilos.botonAnclaje} ${
                              capaSeleccionada.position.anchor === opcion.valor
                                ? estilos.botonAnclajeActivo
                                : ""
                            }`}
                            aria-pressed={
                              capaSeleccionada.position.anchor === opcion.valor
                            }
                            aria-label={opcion.etiqueta}
                            title={opcion.etiqueta}
                            disabled={guardando}
                            onClick={() =>
                              actualizarCapa(capaSeleccionada.id, {
                                ...capaSeleccionada,
                                position: {
                                  ...capaSeleccionada.position,
                                  anchor: opcion.valor,
                                },
                              })
                            }
                          >
                            <span
                              className={estilos.puntoAnclaje}
                              aria-hidden="true"
                            />
                          </button>
                        ))}
                      </div>
                    </div>

                    <p className={estilos.posicionInfo}>
                      Posición: x={capaSeleccionada.position.x.toFixed(3)}, y=
                      {capaSeleccionada.position.y.toFixed(3)}
                    </p>
                  </div>
                )}
              </section>

              {documento.brand && (
                <PaletaColores
                  colores={documento.brand.colors}
                  deshabilitado={guardando}
                  onCambiar={(campo, valor) =>
                    setDocumento((prev) =>
                      prev && prev.brand
                        ? {
                            ...prev,
                            brand: {
                              ...prev.brand,
                              colors: { ...prev.brand.colors, [campo]: valor },
                            },
                          }
                        : prev,
                    )
                  }
                />
              )}

              <PanelVersiones
                token={token}
                proyectoId={proyecto.id}
                disparadorRefresco={disparadorVersiones}
                deshabilitado={guardando || rerenderizando}
                onRestaurado={alRestaurar}
              />
            </div>
          </div>
        )}

        <div className={estilos.pie}>
          {errorGuardado && (
            <div className={estilos.errorBanner} role="alert">
              {errorGuardado}
            </div>
          )}
          {errorRender && (
            <div className={estilos.errorBanner} role="alert">
              {errorRender}
            </div>
          )}
          <div className={estilos.pieAcciones}>
            <button
              type="button"
              className="boton boton-secundario"
              onClick={intentarCerrar}
              disabled={guardando}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="boton boton-secundario"
              onClick={() => void rerenderizar()}
              disabled={!documento || rerenderizando || guardando || hayCambios}
              title={
                hayCambios
                  ? "Guarda los cambios antes de re-renderizar"
                  : undefined
              }
            >
              {rerenderizando ? "Renderizando..." : "Re-renderizar"}
            </button>
            <button
              type="button"
              className="boton boton-primario"
              onClick={() => void guardar()}
              disabled={!documento || !hayCambios || guardando}
            >
              {guardando ? "Guardando..." : "Guardar"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// M3 (docs/plan-calidad-v3.md): fila "label + slider" reutilizada por todos
// los controles numéricos nuevos del panel de propiedades (tracking,
// size_factor, opacidades/grosor de caja y forma, ángulo de degradado,
// tamaño de ícono) -- un solo lugar para el markup en vez de repetirlo ~12
// veces con el mismo esqueleto.
function CampoSlider({
  id,
  etiqueta,
  min,
  max,
  step,
  valor,
  deshabilitado,
  onChange,
}: {
  id: string;
  etiqueta: string;
  min: number;
  max: number;
  step: number;
  valor: number;
  deshabilitado?: boolean;
  onChange: (valor: number) => void;
}): JSX.Element {
  return (
    <div className={estilos.campoPropiedad}>
      <label htmlFor={id}>{etiqueta}</label>
      <input
        id={id}
        type="range"
        min={min}
        max={max}
        step={step}
        value={valor}
        disabled={deshabilitado}
        onChange={(e) => onChange(Number(e.target.value))}
      />
    </div>
  );
}

// M3: punto de partida razonable al presionar "Agregar caja" -- el usuario
// ajusta todo despues con ControlesCajaTexto. Relleno oscuro semitransparente
// (mismo patron que los ejemplos de goldens/*.json) para que se note contra
// cualquier fondo sin tapar el texto.
function cajaTextoPorDefecto(capa: TextLayer): TextBox {
  return {
    fill: capa.color ?? "#000000",
    fill_opacity: 0.55,
    border_opacity: 1,
    border_width: 0,
    corner_radius: 12,
    padding_x_em: 0.6,
    padding_y_em: 0.35,
  };
}

// M3: grupo "Caja" del panel de propiedades de texto -- aislado en su propio
// componente para angostar `capa.box` UNA sola vez (early return); el resto
// de los manejadores leen `caja` ya no-nulo (un identificador simple, a
// diferencia de `capaSeleccionada.box`, SÍ conserva el angostamiento de
// TypeScript dentro de los closures de `onChange`).
function ControlesCajaTexto({
  capa,
  deshabilitado,
  onCambiar,
}: {
  capa: TextLayer;
  deshabilitado: boolean;
  onCambiar: (box: TextBox | undefined) => void;
}): JSX.Element {
  const caja = capa.box;
  if (!caja) {
    return (
      <button
        type="button"
        className="boton boton-secundario"
        disabled={deshabilitado}
        onClick={() => onCambiar(cajaTextoPorDefecto(capa))}
      >
        Agregar caja
      </button>
    );
  }
  const actualizar = (parcial: Partial<TextBox>): void =>
    onCambiar({ ...caja, ...parcial });
  return (
    <>
      <div className={estilos.campoPropiedad}>
        <CampoColorHex
          id="prop-caja-relleno"
          etiqueta="Color de relleno"
          valor={caja.fill}
          onChange={(valor) => actualizar({ fill: valor })}
          deshabilitado={deshabilitado}
        />
      </div>
      <CampoSlider
        id="prop-caja-relleno-opacidad"
        etiqueta={`Opacidad del relleno (${Math.round(caja.fill_opacity * 100)}%)`}
        min={0}
        max={1}
        step={0.05}
        valor={caja.fill_opacity}
        deshabilitado={deshabilitado}
        onChange={(valor) => actualizar({ fill_opacity: valor })}
      />
      <div className={estilos.campoPropiedad}>
        <CampoColorHex
          id="prop-caja-borde"
          etiqueta="Color del borde"
          valor={caja.border_color ?? caja.fill}
          onChange={(valor) => actualizar({ border_color: valor })}
          deshabilitado={deshabilitado}
        />
      </div>
      <CampoSlider
        id="prop-caja-borde-grosor"
        etiqueta={`Grosor del borde (${caja.border_width.toFixed(1)}px)`}
        min={0}
        max={8}
        step={0.5}
        valor={caja.border_width}
        deshabilitado={deshabilitado}
        onChange={(valor) => actualizar({ border_width: valor })}
      />
      <CampoSlider
        id="prop-caja-borde-opacidad"
        etiqueta={`Opacidad del borde (${Math.round(caja.border_opacity * 100)}%)`}
        min={0}
        max={1}
        step={0.05}
        valor={caja.border_opacity}
        deshabilitado={deshabilitado || caja.border_width === 0}
        onChange={(valor) => actualizar({ border_opacity: valor })}
      />
      <div className={estilos.campoPropiedad}>
        <label htmlFor="prop-caja-radio">
          Radio de esquina ({caja.corner_radius}px)
        </label>
        <input
          id="prop-caja-radio"
          type="number"
          min={0}
          max={999}
          step={1}
          value={caja.corner_radius}
          disabled={deshabilitado}
          onChange={(e) =>
            actualizar({
              corner_radius: clamp(Number(e.target.value) || 0, 0, 999),
            })
          }
        />
      </div>
      <CampoSlider
        id="prop-caja-padding-x"
        etiqueta={`Relleno horizontal (${caja.padding_x_em.toFixed(2)}em)`}
        min={0}
        max={3}
        step={0.05}
        valor={caja.padding_x_em}
        deshabilitado={deshabilitado}
        onChange={(valor) => actualizar({ padding_x_em: valor })}
      />
      <CampoSlider
        id="prop-caja-padding-y"
        etiqueta={`Relleno vertical (${caja.padding_y_em.toFixed(2)}em)`}
        min={0}
        max={3}
        step={0.05}
        valor={caja.padding_y_em}
        deshabilitado={deshabilitado}
        onChange={(valor) => actualizar({ padding_y_em: valor })}
      />
      <button
        type="button"
        className="boton boton-peligro"
        disabled={deshabilitado}
        onClick={() => onCambiar(undefined)}
      >
        Quitar caja
      </button>
    </>
  );
}

// M3: degradado de arranque al presionar "Usar degradado" -- mismo color en
// ambas paradas pero opacidad 1->0 para que el efecto se note de inmediato en
// el preview vivo (un degradado con el mismo color Y la misma opacidad en
// las 2 paradas se vería idéntico a `color` solo, sin pista visual de que ya
// está activo).
function degradadoPorDefecto(colorBase: string): GradientSpec {
  return {
    angle_deg: 180,
    stops: [
      { color: colorBase, opacity: 1, at: 0 },
      { color: colorBase, opacity: 0, at: 1 },
    ],
  };
}

// M3: degradado de una capa `shape` -- deliberadamente mínimo (ángulo + color
// de las 2 paradas extremas) para mantenerlo "barato" (docs/plan-calidad-v3.md
// M3, item 3): el esquema admite hasta 4 paradas con opacidad propia cada
// una, pero exponer ese control completo en la UI no se justifica para este
// milestone -- ver limitación documentada en el resumen final.
function ControlesDegradadoForma({
  capa,
  deshabilitado,
  onCambiar,
}: {
  capa: ShapeLayer;
  deshabilitado: boolean;
  onCambiar: (siguiente: ShapeLayer) => void;
}): JSX.Element {
  const gradiente = capa.gradient;
  if (!gradiente) {
    return (
      <button
        type="button"
        className="boton boton-secundario"
        disabled={deshabilitado}
        onClick={() =>
          onCambiar({ ...capa, gradient: degradadoPorDefecto(capa.color) })
        }
      >
        Usar degradado
      </button>
    );
  }
  const actualizarStop = (
    indice: number,
    parcial: Partial<GradientStop>,
  ): void => {
    const stops = gradiente.stops.map((s, i) =>
      i === indice ? { ...s, ...parcial } : s,
    );
    onCambiar({ ...capa, gradient: { ...gradiente, stops } });
  };
  const inicio = gradiente.stops[0];
  const indiceFinal = gradiente.stops.length - 1;
  const fin = gradiente.stops[indiceFinal];
  return (
    <>
      <CampoSlider
        id="prop-forma-degradado-angulo"
        etiqueta={`Ángulo (${gradiente.angle_deg}°)`}
        min={0}
        max={360}
        step={5}
        valor={gradiente.angle_deg}
        deshabilitado={deshabilitado}
        onChange={(valor) =>
          onCambiar({ ...capa, gradient: { ...gradiente, angle_deg: valor } })
        }
      />
      <div className={estilos.campoPropiedad}>
        <CampoColorHex
          id="prop-forma-degradado-inicio"
          etiqueta="Color inicial"
          valor={inicio.color}
          onChange={(valor) => actualizarStop(0, { color: valor })}
          deshabilitado={deshabilitado}
        />
      </div>
      <div className={estilos.campoPropiedad}>
        <CampoColorHex
          id="prop-forma-degradado-fin"
          etiqueta="Color final"
          valor={fin.color}
          onChange={(valor) => actualizarStop(indiceFinal, { color: valor })}
          deshabilitado={deshabilitado}
        />
      </div>
      <button
        type="button"
        className="boton boton-peligro"
        disabled={deshabilitado}
        onClick={() => onCambiar({ ...capa, gradient: undefined })}
      >
        Quitar degradado
      </button>
    </>
  );
}

// M3: controles de borde (siempre visibles) + degradado (grupo colapsable)
// de una capa `shape`.
function PropiedadesForma({
  capa,
  deshabilitado,
  onCambiar,
}: {
  capa: ShapeLayer;
  deshabilitado: boolean;
  onCambiar: (siguiente: ShapeLayer) => void;
}): JSX.Element {
  return (
    <>
      <div className={estilos.campoPropiedad}>
        <CampoColorHex
          id="prop-forma-borde"
          etiqueta="Color del borde"
          valor={capa.border_color ?? capa.color}
          onChange={(valor) => onCambiar({ ...capa, border_color: valor })}
          deshabilitado={deshabilitado}
        />
      </div>
      <CampoSlider
        id="prop-forma-borde-grosor"
        etiqueta={`Grosor del borde (${capa.border_width.toFixed(1)}px)`}
        min={0}
        max={8}
        step={0.5}
        valor={capa.border_width}
        deshabilitado={deshabilitado}
        onChange={(valor) => onCambiar({ ...capa, border_width: valor })}
      />
      <CampoSlider
        id="prop-forma-borde-opacidad"
        etiqueta={`Opacidad del borde (${Math.round(capa.border_opacity * 100)}%)`}
        min={0}
        max={1}
        step={0.05}
        valor={capa.border_opacity}
        deshabilitado={deshabilitado || capa.border_width === 0}
        onChange={(valor) => onCambiar({ ...capa, border_opacity: valor })}
      />
      {capa.border_width > 0 && (
        <button
          type="button"
          className="boton boton-secundario"
          disabled={deshabilitado}
          onClick={() =>
            onCambiar({ ...capa, border_color: undefined, border_width: 0 })
          }
        >
          Quitar borde
        </button>
      )}

      <details className={estilos.grupoColapsable}>
        <summary className={estilos.grupoColapsableResumen}>
          Degradado{capa.gradient ? "" : " (sin usar)"}
        </summary>
        <div className={estilos.grupoColapsableCuerpo}>
          <ControlesDegradadoForma
            capa={capa}
            deshabilitado={deshabilitado}
            onCambiar={onCambiar}
          />
        </div>
      </details>
    </>
  );
}

// M3: slider de tamaño de un IconLayer -- "fijo 200px" (docs/hueco-vocabulario.md
// #9) mientras `size` esté ausente; el botón "Personalizado" arranca el
// slider en la fracción EQUIVALENTE a 200px del lienzo actual, para que
// activarlo no salte el tamaño visual del ícono.
function ControlesTamanoIcono({
  capa,
  canvasWidth,
  deshabilitado,
  onCambiar,
}: {
  capa: IconLayer;
  canvasWidth: number;
  deshabilitado: boolean;
  onCambiar: (siguiente: IconLayer) => void;
}): JSX.Element {
  const personalizado = capa.size != null;
  const fraccionMostrada = capa.size ?? clamp(200 / canvasWidth, 0.03, 0.6);
  return (
    <div className={estilos.campoPropiedad}>
      <div className={estilos.filaEtiquetaToggle}>
        <label htmlFor="prop-icono-tamano">
          Tamaño
          {personalizado
            ? ` (${Math.round(fraccionMostrada * canvasWidth)}px)`
            : " (fijo 200px)"}
        </label>
        <button
          type="button"
          className={`${estilos.botonToggle} ${
            personalizado ? estilos.botonToggleActivo : ""
          }`}
          aria-pressed={personalizado}
          disabled={deshabilitado}
          onClick={() =>
            onCambiar({
              ...capa,
              size: personalizado ? undefined : fraccionMostrada,
            })
          }
        >
          Personalizado
        </button>
      </div>
      <input
        id="prop-icono-tamano"
        type="range"
        min={0.03}
        max={0.6}
        step={0.01}
        value={fraccionMostrada}
        disabled={deshabilitado || !personalizado}
        onChange={(e) => onCambiar({ ...capa, size: Number(e.target.value) })}
      />
    </div>
  );
}
